import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { StratosMedia } from "../../../../modules/content/components/StratosMedia/StratosMedia";
import { StratosRichText } from "../../../../modules/content/components/StratosRichText/StratosRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-stratos-body text-xs text-cloud-white/80 transition-colors hover:text-cloud-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terracotta";
  return (
    <footer
      className={`${parentClass || ""} relative overflow-hidden bg-night-flight text-cloud-white before:absolute before:left-0 before:top-0 before:h-3 before:w-2/5 before:bg-horizon-blue`}
    >
      <div className="stratos-container py-20 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-[1.35fr_1.65fr_0.7fr]">
          {" "}
          <div>
            {logo ? (
              <StratosMedia
                className="mb-8 h-14 w-56 bg-transparent"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <StratosRichText
                className="max-w-sm text-base text-cloud-white/80 md:text-lg"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-stratos-body text-xs leading-6 text-cloud-white/80">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 font-stratos-display text-sm font-bold">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-cloud-white/35 px-4 font-stratos-body text-xs hover:bg-cloud-white hover:text-night-flight focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terracotta"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? <Icon className="size-4" name={item.icon} /> : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-16 flex flex-col gap-3 border-t border-cloud-white/25 pt-8 font-stratos-body text-xs text-cloud-white/70 sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
